import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { BASE_URL } from "@funded-here-interface/common/src/constant/constant";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { Modal, Text, Button as MButton, Group } from "@mantine/core";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useWithdrawalFund from "../../hooks/useWithdrawalFund";
import useWithdrawalFundConfirmed from "../../hooks/useWithdrawalFundConfirmed";
import { COLORS } from "../../constants/colors";
import { useDisclosure } from "@mantine/hooks";
import useInvestorGA from "../../hooks/useInvestorGA";
import useInvestorFunds from "../../hooks/useInvestorAccount";
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import useWithdrawal from "../../hooks/useWithdrawal";
import { MRT_ColumnDef } from "mantine-react-table";
import { toToastItem } from "react-toastify/dist/utils";
import { formatNumbersWithThousandSeperators } from "@funded-here-interface/common/src/Utils/formatter";

export enum Status {
  ERROR = "error",
  PENDING_APPROVAL = "pending-approval",
  APPROVED = "approved",
  PENDING_APPROVAL_BY_PSP = "pending-approval-by-psp",
  PENDING_APPROVAL2_BY_PSP = "pending-approval2-by-psp",
  COMPLETE = "complete",
  REJECTED = "rejected",
  CANCEL = "cancel",
  REJECTED_BY_SERVICE_PROVIDER = "rejected-by-psp",
}
interface Withdrawal {
  id: number;
  amount: number;
  feesIncurred: number;
  currency: string;
  status: string;
  createdDate: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  actionDate: string;
}

interface WithdrawalDetail {
  totalFee: string;
  virtualAccountBalance: string;
}

interface AccountData {
  accountBalance: {
    SGD?: number;
    USD?: number;
    [key: string]: number | undefined;
  };
}

const ProcessingWithdrawals = () => {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [isError, setError] = useState<string | null>(null);
  const [isWithdrawalLoading, setIsWithdrawalLoading] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState("SGD");
  const [withdrawalAmount, setWithdrawalAmount] = useState<number>(0);
  const [withdrawalFundDetail, setWithdrawalFundDetail] =
    useState<WithdrawalDetail>();
  const [accountData, setAccountData] = useState<any>({
    accountBalance: { SGD: 0, USD: 0 },
  });
  const [accountFHData, setAccountFHData] = useState<any>({
    accountBalance: { SGD: 0, USD: 0 },
  });

  const { token, isAuthRep, entityType } = useSelector(
    (state: RootState) => state.auth
  );
  const isIndividual = entityType === "individual";
  const [
    openedWithdraw,
    { open: openWithdrawModal, close: closeWithdrawModal },
  ] = useDisclosure(false);

  const [displayValue, setDisplayValue] = useState<string>("");

  const withdrawalFund = useWithdrawalFund(
    token,
    selectedCurrency,
    withdrawalAmount,
    Number(withdrawalFundDetail?.totalFee)
  );

  const withdrawalFundConfirmed = useWithdrawalFundConfirmed();
  const isMounted = useRef(true);
  const InvestorGAMutation = useInvestorGA();
  const InvestorFundsMutation = useInvestorFunds();
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const [totalRowCount, setTotalRowCount] = useState(0);

  const [isConfirmClicked, setIsConfirmClicked] = useState(false);

  const apiColumnFilters: any = JSON.stringify(
    columnFilters
      .filter((filter) => {
        if (Array.isArray(filter.value)) {
          return filter.value.every(
            (v) => v !== null && v !== undefined && v !== ""
          );
        }
        return (
          filter.value !== null &&
          filter.value !== undefined &&
          filter.value !== ""
        );
      })
      .map((filter) => {
        let column = filter.id;
        let value = filter.value;

        return {
          [column]: value,
        };
      })
  );

  const apiSorting: any = JSON.stringify(
    sorting.reduce((acc, sort) => {
      if (sort.id && sort.desc !== undefined) {
        acc[sort.id] = sort.desc;
      }
      return acc;
    }, {} as Record<string, any>)
  );

  const { data, isLoading, error, refetch } = useWithdrawal(
    token,
    pagination,
    apiColumnFilters,
    apiSorting
  );

  useEffect(() => {
    isMounted.current = true;

    const fetchData = async () => {
      try {
        if (!token) throw new Error("No authentication token found");

        if (data) {
          if (isMounted.current) {
            setWithdrawals(data.data);
            setTotalRowCount(data.meta.totalRowCount);
          }
        }

        if (error) {
          toast.error((error as Error).message);
        }

        InvestorFundsMutation.mutate(
          { token },
          {
            onSuccess: (data) => {
              if (isMounted.current) {
                setAccountFHData(data);
                setLoading(false);
              }
            },
            onError: (error: any) => {
              if (isMounted.current) {
                setError(error.message || "Failed to fetch account data");
                setLoading(false);
              }
            },
          }
        );
        InvestorGAMutation.mutate(
          { token },
          {
            onSuccess: (data) => {
              if (isMounted.current) {
                setAccountData(data);
                setLoading(false);
              }
            },
            onError: (error: any) => {
              if (isMounted.current) {
                setError(error.message || "Failed to fetch account data");
                setLoading(false);
              }
            },
          }
        );
      } catch (err: unknown) {
        if (isMounted.current) {
          setError(
            err instanceof Error ? err.message : "An unknown error occurred"
          );
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted.current = false;
    };
  }, [token, data, isLoading, error]);

  const handleWithdrawalClick = () => {
    if (!token) return;
    setIsWithdrawalLoading(true);
    withdrawalFund
      .refetch({ throwOnError: true })
      .then((resp) => {
        if (isMounted.current) {
          setWithdrawalFundDetail(resp.data);
          openWithdrawModal();
          setIsWithdrawalLoading(false);
        }
      })
      .catch((error) => {
        if (isMounted.current) {
          toast.error(error.message || "Failed to fetch withdrawal details");
          setIsWithdrawalLoading(false);
        }
      });
  };

  const handleConfirmWithdrawalClick = () => {
    if (!token || isConfirmClicked) return;
    setIsConfirmClicked(true);

    const totalFee = Number(withdrawalFundDetail?.totalFee) || 0;
    const maxBalance =
      Number(accountFHData?.accountBalance?.[selectedCurrency]) || 0;

    const roundedWithdrawalAmount = Number(withdrawalAmount.toFixed(2)) || 0;
    const roundedMaxBalance = Number(maxBalance.toFixed(2)) || 0;
    const roundedTotalFee = Number(totalFee.toFixed(2)) || 0;

    if (roundedWithdrawalAmount <= 0) {
      toast.error("Please enter a valid withdrawal amount.");
      setIsConfirmClicked(false);
      return;
    }

    if (roundedWithdrawalAmount > roundedMaxBalance) {
      toast.error("Withdrawal amount exceeds available balance.");
      setIsConfirmClicked(false);
      return;
    }

    if (roundedWithdrawalAmount < roundedTotalFee) {
      toast.error("Withdrawal amount is not enough to cover the fee.");
      setIsConfirmClicked(false);
      return;
    }

    withdrawalFundConfirmed.mutate(
      {
        token,
        currency: selectedCurrency,
        amount: roundedWithdrawalAmount,
        totalFee: roundedTotalFee,
      },
      {
        onSuccess: async (response) => {
          if (isMounted.current) {
            const finalTransferAmount = Number(response.finalTransferAmount);
            const newBalance = Number(
              (roundedMaxBalance - roundedWithdrawalAmount).toFixed(2)
            );
            setAccountFHData((prevData: AccountData) => ({
              ...prevData,
              accountBalance: {
                ...prevData.accountBalance,
                [selectedCurrency]: newBalance,
              },
            }));
            toast.info(`Withdrawal request submitted successfully`);
            InvestorFundsMutation.mutate(
              { token },
              {
                onSuccess: (data) => {
                  if (isMounted.current) {
                    setAccountFHData(data);
                  }
                },
                onError: (error: any) => {
                  if (isMounted.current) {
                    toast.error(
                      error.message || "Failed to fetch updated account data"
                    );
                  }
                },
              }
            );
            await refetch();
            setWithdrawalAmount(0);
            setDisplayValue("");
            closeWithdrawModal();
          }
          setIsConfirmClicked(false);
        },
        onError: (error: any) => {
          if (isMounted.current) {
            toast.error(error.message || "Withdrawal request failed");
          }
          setIsConfirmClicked(false);
        },
      }
    );
  };

  const handleWithdrawalAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const maxBalance = accountFHData?.accountBalance?.[selectedCurrency] || 0;

    if (value === "") {
      setWithdrawalAmount(0);
      setDisplayValue("");
      return;
    }

    const regex = /^\d*(\.\d{0,2})?$/;
    if (!regex.test(value)) {
      return;
    }

    const parsedValue = parseFloat(value);
    if (isNaN(parsedValue) || parsedValue < 0) {
      toast.error("Please enter a valid positive number.");
      return;
    }

    const clampedValue = Math.min(parsedValue, maxBalance);
    if (isMounted.current) {
      setWithdrawalAmount(clampedValue);
      setDisplayValue(value);
    }
  };

  const handleBlur = () => {
    if (withdrawalAmount === 0 || displayValue === "") {
      setWithdrawalAmount(0);
      setDisplayValue("");
      return;
    }

    const formattedValue = Number(withdrawalAmount.toFixed(2));
    setWithdrawalAmount(formattedValue);
    setDisplayValue(formattedValue.toFixed(2));
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending-approval":
        return "#ff9800";
      case "approved":
        return "#28a745";
      case "processing":
        return "#1976d2";
      case "complete":
        return "#4caf50";
      case "error":
      case "rejected":
        return "#dc3545";
      case "cancel":
        return "#9e9e9e";
      default:
        return "#666";
    }
  };

  const columns = useMemo<MRT_ColumnDef<Withdrawal>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Transaction ID",
        size: 100,
      },
      {
        accessorKey: "amount",
        header: "Withdrawal Amount",
        size: 150,
        sortingFn: "alphanumeric",
        Cell: ({ cell }) => (
          <p>
            {cell.getValue<string>()
              ? formatNumbersWithThousandSeperators(cell.getValue<number>())
              : "-"}
          </p>
        ),
      },
      {
        accessorKey: "feesIncurred",
        header: "Transaction Fee",
        size: 120,
        Cell: ({ cell }) => (
          <p>
            {cell.getValue<string>()
              ? formatNumbersWithThousandSeperators(cell.getValue<number>())
              : "-"}
          </p>
        ),
      },
      {
        accessorKey: "netAmount",
        header: "Net Amount",
        size: 120,
        Cell: ({ cell }) => (
          <p>
            {cell.getValue<string>()
              ? formatNumbersWithThousandSeperators(cell.getValue<number>())
              : "-"}
          </p>
        ),
      },
      {
        accessorKey: "currency",
        header: "Currency",
        size: 100,
      },
      {
        accessorKey: "bankName",
        header: "Bank Name",
        size: 150,
      },
      {
        accessorKey: "accountName",
        header: "Account Name",
        size: 150,
      },
      {
        accessorKey: "accountNumber",
        header: "Account Number",
        size: 150,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => {
          const status = cell.getValue<string>().toLowerCase();
          let displayStatus: string;

          switch (status) {
            case Status.ERROR.toLowerCase():
            case Status.REJECTED_BY_SERVICE_PROVIDER.toLowerCase():
              displayStatus = Status.REJECTED;
              break;
            case Status.PENDING_APPROVAL_BY_PSP.toLowerCase():
            case Status.PENDING_APPROVAL2_BY_PSP.toLowerCase():
              displayStatus = Status.PENDING_APPROVAL;
              break;
            default:
              displayStatus = cell.getValue<string>();
              break;
          }

          return (
            <Text style={{ color: getStatusColor(displayStatus) }}>
              {displayStatus}
            </Text>
          );
        },
      },
      {
        accessorKey: "createdDate",
        header: "Withdrawal Request Date",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          new Date(cell.getValue<string>())
            .toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
            .replace(",", "") || "Invalid Date",
        sortingFn: "datetime",
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableColumnActions: false,
    enableColumnFilters: true,
    enableDensityToggle: false,
    enableMultiSort: true,
    paginationDisplayMode: "pages",
    state: { pagination, isLoading, sorting },
    columns,
    data: data?.data ?? [],
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    rowCount: totalRowCount,
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
      withBorder: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
  });
  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "20px", fontSize: "18px" }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "20px",
          color: "#d32f2f",
          fontSize: "18px",
        }}
      >
        Error: {isError}
      </div>
    );
  }

  if (isAuthRep || isIndividual) {
    return (
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "20px",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <h1
          style={{
            fontWeight: 800,
            color: "#333",
            marginBottom: "20px",
            fontSize: "28px",
          }}
        >
          Withdrawal
        </h1>

        <div style={{ marginBottom: "20px" }}>
          <div style={{ marginTop: "20px" }}>
            <Text style={{ fontWeight: 800, marginBottom: "10px" }}>
              Available Balance:{" "}
              <span style={{ color: COLORS.agreementFont }}>
                {selectedCurrency}{" "}
                {formatNumbersWithThousandSeperators(
                  Number(accountFHData?.accountBalance?.[selectedCurrency]) ||
                    0,
                  true,
                  undefined,
                  "SGD"
                )}
              </span>
            </Text>
            <Text style={{ fontWeight: 800, marginBottom: "10px" }}>
              Amount to Withdraw:
            </Text>
            <input
              type="text" // Change to text to control display precisely
              value={displayValue}
              onChange={handleWithdrawalAmountChange}
              onBlur={handleBlur}
              placeholder="0.00"
              disabled={!token}
              style={{
                padding: "10px 15px",
                fontSize: "16px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                outline: "none",
                width: "200px",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                marginRight: "10px",
              }}
            />
            <MButton
              style={{ backgroundColor: "#2A7D71" }}
              onClick={handleWithdrawalClick}
              loading={isWithdrawalLoading}
              disabled={
                !token ||
                withdrawalAmount === 0 ||
                (accountData?.accountBalance?.[selectedCurrency] ?? 0) <= 0
              }
            >
              Withdraw Funds
            </MButton>
          </div>
        </div>
        <MantineReactTable table={table} />

        <Modal
          opened={openedWithdraw}
          onClose={closeWithdrawModal}
          size="lg"
          centered
          overlayProps={{ blur: 3, color: "rgba(0, 0, 0, 0.5)" }}
          withCloseButton
          radius="md"
          padding="xl"
          styles={{
            content: {
              backgroundColor: "#FFFFFF",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
              borderRadius: "12px",
            },
            header: {
              backgroundColor: "#F5F7FA",
              borderBottom: "1px solid #E0E0E0",
              padding: "16px 24px",
              borderRadius: "12px 12px 0 0",
            },
            body: { padding: "24px" },
          }}
        >
          <Text
            ta="center"
            style={{
              color: COLORS.agreementFont,
              fontWeight: 700,
              fontSize: "24px",
              marginBottom: "16px",
            }}
          >
            Withdrawal Confirmation
          </Text>

          <div
            style={{
              backgroundColor: "#F9F9F9",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "20px",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Text size="lg" weight={600} mb="md" style={{ color: "#333" }}>
              Transfer to:
            </Text>
            <div style={{ marginBottom: "12px" }}>
              <Text size="sm" style={{ color: "#666", fontWeight: 500 }}>
                Bank Name: {accountData?.investorBankName ?? "-"}
              </Text>
              <Text size="sm" style={{ color: "#666", fontWeight: 500 }}>
                Account Name: {accountData?.investorBankAccountHolder ?? "-"}
              </Text>
              <Text size="sm" style={{ color: "#666", fontWeight: 500 }}>
                Account Number: {accountData?.investorBankAccount ?? "-"}
              </Text>
              <Text
                size="sm"
                style={{
                  color: COLORS.agreementFont,
                  fontWeight: 600,
                  marginTop: "8px",
                }}
              >
                Withdrawal Amount: {selectedCurrency}{" "}
                {withdrawalAmount > 0
                  ? formatNumbersWithThousandSeperators(withdrawalAmount, true)
                  : "0.00"}
              </Text>
              <Text
                size="sm"
                style={{
                  color: COLORS.agreementFont,
                  fontWeight: 600,
                  marginTop: "8px",
                }}
              >
                Amount You Will Receive: {selectedCurrency}{" "}
                {formatNumbersWithThousandSeperators(
                  withdrawalAmount -
                    Number(withdrawalFundDetail?.totalFee || 0),
                  true
                )}
              </Text>
            </div>
          </div>

          <Text
            size="sm"
            ta="justify"
            style={{ color: "#757575", marginBottom: "24px" }}
          >
            {withdrawalAmount < Number(withdrawalFundDetail?.totalFee || 0)
              ? `Please note that ${selectedCurrency} ${
                  withdrawalFundDetail?.totalFee === ""
                    ? "0"
                    : Number(withdrawalFundDetail?.totalFee).toLocaleString(
                        "en-GB",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      )
                } will be charged by our payment service provider for this withdrawal. Your withdrawal request cannot be processed as the transaction fee exceeds the requested withdrawal amount.`
              : `Please note that ${selectedCurrency} ${
                  withdrawalFundDetail?.totalFee === ""
                    ? "0"
                    : Number(withdrawalFundDetail?.totalFee).toLocaleString(
                        "en-GB",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      )
                } will be charged by our payment service provider for this withdrawal. Your withdrawal request will be processed within two (2) working days.`}
          </Text>

          <Group mt="xl" position="center" spacing="xl">
            <MButton
              onClick={closeWithdrawModal}
              color="gray"
              variant="outline"
              radius="md"
              size="md"
              disabled={!token}
              styles={{
                root: {
                  borderColor: "#757575",
                  color: "#757575",
                  "&:hover": {
                    backgroundColor: "rgba(117, 117, 117, 0.1)",
                    borderColor: "#666",
                  },
                },
              }}
            >
              Cancel
            </MButton>
            <MButton
              onClick={handleConfirmWithdrawalClick}
              color="blue"
              variant="filled"
              radius="md"
              size="md"
              loading={isConfirmClicked}
              disabled={
                !token ||
                withdrawalAmount <
                  Number(withdrawalFundDetail?.totalFee || 0) ||
                isConfirmClicked
              }
              styles={{
                root: {
                  backgroundColor: COLORS.agreementFont,
                  "&:hover": { backgroundColor: "#2A7D71" },
                },
              }}
            >
              Confirm
            </MButton>
          </Group>
        </Modal>

        <ToastContainer />
      </div>
    );
  } else {
    return (
      <div>
        <h1>Permission Denied</h1>
        For more information, please contact
        <a className="font-bold"> FundedHere</a>
      </div>
    );
  }
};

export default ProcessingWithdrawals;
